import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { User, Mail, Phone, ShoppingBag, CreditCard, Check, ArrowLeft, Shield, Zap, Tag, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "test";
const DISCOUNT_PERCENT = 40;

// US States for tax selection
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "Washington D.C." }
];

const Checkout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [isOfferActive, setIsOfferActive] = useState(false);
  const [savedAmount, setSavedAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'paypal'
  const [stripeProcessing, setStripeProcessing] = useState(false);

  // Tax calculation state
  const [selectedState, setSelectedState] = useState('');
  const [taxInfo, setTaxInfo] = useState(null);
  const [calculatingTax, setCalculatingTax] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchCartItems();
    checkOfferStatus();
    checkStripeReturn();
  }, []);

  // Check if returning from Stripe
  const checkStripeReturn = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setLoading(true);
      await pollPaymentStatus(sessionId);
    }
  };

  // Poll Stripe payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setOrderNumber(data.order_number || 'Processing...');
        setOrderComplete(true);
        
        // Clear cart
        localStorage.removeItem('cart_session_id');
        localStorage.setItem('cart_count', '0');
        window.dispatchEvent(new Event('cartUpdated'));
        
        toast.success('Payment successful!');
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setLoading(false);
        navigate('/checkout');
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status.');
      setLoading(false);
    }
  };

  const checkOfferStatus = () => {
    const offerEndDate = localStorage.getItem('offer_end_date');
    if (offerEndDate) {
      const endDate = new Date(offerEndDate);
      const now = new Date();
      setIsOfferActive(now < endDate);
    }
  };

  const getSessionId = () => {
    return localStorage.getItem('cart_session_id') || '';
  };

  const fetchCartItems = async () => {
    // Don't fetch if returning from Stripe
    if (searchParams.get('session_id')) return;

    // Check for direct buy (from URL params)
    const serviceId = searchParams.get('service_id');
    const serviceName = searchParams.get('service_name');
    const packageId = searchParams.get('package_id');
    const packageName = searchParams.get('package_name');
    const price = searchParams.get('price');

    if (serviceId && serviceName && packageId && packageName && price) {
      // Direct buy mode - create cart item from URL params
      const directItem = {
        id: `direct-${Date.now()}`,
        service_id: serviceId,
        service_name: serviceName,
        package_id: packageId,
        package_name: packageName,
        price: parseFloat(price)
      };
      setCartItems([directItem]);
      setLoading(false);
      return;
    }

    // Otherwise fetch from cart
    const sessionId = getSessionId();
    if (!sessionId) {
      navigate('/cart');
      return;
    }

    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      if (response.data.length === 0) {
        toast.error('Your cart is empty');
        navigate('/cart');
        return;
      }
      setCartItems(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  // Calculate tax when state changes
  useEffect(() => {
    if (selectedState && cartItems.length > 0) {
      calculateTax();
    } else {
      setTaxInfo(null);
    }
  }, [selectedState, cartItems, isOfferActive]);

  const calculateTax = async () => {
    setCalculatingTax(true);
    try {
      const response = await axios.post(`${API}/payments/calculate-tax`, {
        subtotal: calculateSubtotal(),
        state_code: selectedState,
        discount_percent: isOfferActive ? DISCOUNT_PERCENT : 0
      });
      setTaxInfo(response.data);
    } catch (error) {
      console.error('Error calculating tax:', error);
      toast.error('Error calculating tax');
    } finally {
      setCalculatingTax(false);
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + item.price, 0);
  };

  const calculateDiscount = () => {
    if (!isOfferActive) return 0;
    return calculateSubtotal() * (DISCOUNT_PERCENT / 100);
  };

  const calculateDiscountedSubtotal = () => {
    return calculateSubtotal() - calculateDiscount();
  };

  const getTaxRate = () => {
    return taxInfo?.tax_rate || 0.08;
  };

  const calculateTaxAmount = () => {
    if (taxInfo) return taxInfo.tax_amount;
    return calculateDiscountedSubtotal() * getTaxRate();
  };

  const calculateTotal = () => {
    if (taxInfo) return taxInfo.total;
    return calculateDiscountedSubtotal() + calculateTaxAmount();
  };

  const getDiscountedPrice = (price) => {
    if (!isOfferActive) return price;
    return price * (1 - DISCOUNT_PERCENT / 100);
  };

  const handleChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  // Stripe card payment
  const handleStripePayment = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all customer information');
      return;
    }

    if (!selectedState) {
      toast.error('Please select your state for tax calculation');
      return;
    }

    setStripeProcessing(true);

    try {
      const response = await axios.post(`${API}/payments/stripe/create-checkout`, {
        cart_session_id: getSessionId(),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        state_code: selectedState,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
      setStripeProcessing(false);
    }
  };

  // PayPal payment handlers - Using Backend APIs for secure capture
  const createOrder = async (data, actions) => {
    try {
      // Create order via backend for server-side verification
      const response = await axios.post(`${API}/payments/paypal/create-order`, {
        cart_session_id: getSessionId(),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        state_code: selectedState
      });
      
      return response.data.id; // Return PayPal order ID
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      toast.error('Failed to create payment. Please try again.');
      throw error;
    }
  };

  const onApprove = async (data, actions) => {
    setProcessing(true);
    
    try {
      // Capture payment via backend for secure verification
      const response = await axios.post(`${API}/payments/paypal/capture-order`, {
        order_id: data.orderID,
        cart_session_id: getSessionId(),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        state_code: selectedState
      });
      
      if (response.data.success) {
        const discount = calculateDiscount();
        setSavedAmount(discount);
        
        // Clear cart locally
        localStorage.removeItem('cart_session_id');
        localStorage.setItem('cart_count', '0');
        window.dispatchEvent(new Event('cartUpdated'));

        setOrderNumber(response.data.order_number);
        setOrderComplete(true);
        
        toast.success('Payment Successful!');
      } else {
        throw new Error('Payment capture failed');
      }

    } catch (error) {
      console.error('Error processing PayPal order:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  const onError = (err) => {
    console.error('PayPal Error:', err);
    toast.error('Payment failed. Please try again.');
  };

  const isFormValid = customerInfo.name && customerInfo.email && customerInfo.phone && selectedState;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-dark)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
          <div className="text-[var(--text-muted)]">Loading...</div>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
        <Header />
        <section className="pt-32 pb-16 flex items-center justify-center">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-8 sm:p-12">
              <div className="w-24 h-24 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-8 border-4 border-[#10B981]">
                <Check size={48} className="text-[#10B981]" />
              </div>
              
              <h1 className="text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-4">
                Order Complete!
              </h1>
              
              <p className="text-lg sm:text-xl text-[var(--text-secondary)] mb-6">
                Thank you for your purchase{customerInfo.name ? `, ${customerInfo.name}` : ''}!
              </p>
              
              <div className="bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl p-6 mb-6">
                <p className="text-[var(--text-muted)] mb-2">Order Number</p>
                <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                  {orderNumber}
                </p>
              </div>

              {savedAmount > 0 && (
                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 mb-6">
                  <p className="text-[#10B981] font-bold text-lg flex items-center justify-center gap-2">
                    <Zap size={20} fill="currentColor" />
                    You saved ${savedAmount.toFixed(2)} with the limited time offer!
                  </p>
                </div>
              )}
              
              <p className="text-[var(--text-muted)] mb-8">
                We've sent a confirmation email{customerInfo.email ? ` to ${customerInfo.email}` : ''}. 
                Our team will contact you within 24 hours.
              </p>
              
              <button
                onClick={() => navigate('/')}
                data-testid="back-to-home-btn"
                className="px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              >
                Back to Home
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <Header />
      
      <section className="pt-32 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/cart')}
              data-testid="back-to-cart-btn"
              className="flex items-center gap-2 text-[#3B82F6] hover:text-[var(--text-primary)] transition-colors duration-300 mb-6"
            >
              <ArrowLeft size={20} />
              <span>Back to Cart</span>
            </button>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/10 border border-[#10B981]/30 rounded-full mb-6">
              <Shield size={16} className="text-[#10B981]" />
              <span className="text-[#10B981] text-sm font-semibold">Secure Checkout</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4" style={{ letterSpacing: '-0.02em' }}>
              Complete Your Purchase
            </h1>
          </div>

          {/* Discount Banner */}
          {isOfferActive && (
            <div className="mb-8 p-4 bg-gradient-to-r from-[#F97316]/20 via-[#EC4899]/20 to-[#8B5CF6]/20 border border-[#EC4899]/30 rounded-2xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#F97316] to-[#EC4899] flex items-center justify-center flex-shrink-0">
                  <Zap size={20} className="text-white" fill="white" />
                </div>
                <div className="flex-1">
                  <p className="text-white font-bold text-lg">{DISCOUNT_PERCENT}% OFF Applied!</p>
                  <p className="text-white/70 text-sm">Your discount has been applied to all items</p>
                </div>
                <div className="text-right">
                  <p className="text-[#10B981] font-bold text-xl">-${calculateDiscount().toFixed(2)}</p>
                  <p className="text-white/50 text-xs">Total savings</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Customer Information & Payment */}
            <div className="lg:col-span-2 space-y-8">
              {/* Customer Information */}
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-primary)] mb-6 flex items-center gap-3">
                  <User size={24} className="text-[#3B82F6]" />
                  Customer Information
                </h2>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-[var(--text-secondary)] mb-2 text-sm font-medium">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      data-testid="checkout-name"
                      value={customerInfo.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-[var(--text-secondary)] mb-2 text-sm font-medium">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      data-testid="checkout-email"
                      value={customerInfo.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-[var(--text-secondary)] mb-2 text-sm font-medium">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="checkout-phone"
                      value={customerInfo.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] transition-colors"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  {/* State Selection for Tax */}
                  <div>
                    <label htmlFor="state" className="block text-[var(--text-secondary)] mb-2 text-sm font-medium flex items-center gap-2">
                      <MapPin size={16} className="text-[#F97316]" />
                      State (for tax calculation) *
                    </label>
                    <select
                      id="state"
                      name="state"
                      data-testid="checkout-state"
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      required
                      className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#F97316] transition-colors"
                    >
                      <option value="">Select your state</option>
                      {US_STATES.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {taxInfo && (
                      <p className="text-sm text-[var(--text-muted)] mt-2">
                        Tax rate for {taxInfo.state_name}: {taxInfo.tax_rate_percent}
                        {taxInfo.tax_rate === 0 && " (No sales tax!)"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-primary)] mb-6 flex items-center gap-3">
                  <CreditCard size={24} className="text-[#8B5CF6]" />
                  Payment Method
                </h2>

                {!isFormValid ? (
                  <div className="text-center py-8 bg-[var(--bg-dark)] rounded-xl border border-[var(--border)]">
                    <p className="text-[var(--text-muted)]">Please fill in all required information above to proceed with payment</p>
                  </div>
                ) : (
                  <>
                    {/* Payment Method Tabs */}
                    <div className="flex gap-4 mb-6">
                      <button
                        onClick={() => setPaymentMethod('card')}
                        data-testid="payment-method-card"
                        className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                          paymentMethod === 'card'
                            ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white'
                            : 'bg-[var(--bg-dark)] border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-hover)]'
                        }`}
                      >
                        <CreditCard size={20} />
                        Credit Card
                      </button>
                      <button
                        onClick={() => setPaymentMethod('paypal')}
                        data-testid="payment-method-paypal"
                        className={`flex-1 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                          paymentMethod === 'paypal'
                            ? 'bg-[#0070BA] text-white'
                            : 'bg-[var(--bg-dark)] border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-hover)]'
                        }`}
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                        </svg>
                        PayPal
                      </button>
                    </div>

                    {/* Card Payment */}
                    {paymentMethod === 'card' && (
                      <div className="space-y-4">
                        <p className="text-[var(--text-muted)] text-sm mb-4">
                          You'll be redirected to our secure payment page to complete your purchase.
                        </p>
                        <button
                          onClick={handleStripePayment}
                          disabled={stripeProcessing}
                          data-testid="stripe-pay-btn"
                          className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-[#3B82F6]/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                          style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                        >
                          {stripeProcessing ? (
                            <>
                              <Loader2 size={20} className="animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard size={20} />
                              Pay ${calculateTotal().toFixed(2)} with Card
                            </>
                          )}
                        </button>
                        <div className="flex items-center justify-center gap-2 text-[var(--text-muted)] text-sm">
                          <Shield size={14} />
                          <span>Secured by Stripe</span>
                        </div>
                      </div>
                    )}

                    {/* PayPal Payment */}
                    {paymentMethod === 'paypal' && (
                      <PayPalScriptProvider options={{ 
                        "client-id": PAYPAL_CLIENT_ID, 
                        currency: "USD",
                        intent: "capture"
                      }}>
                        <PayPalButtons
                          style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                          createOrder={async (data, actions) => {
                            try {
                              // Create order via backend
                              const response = await axios.post(`${API}/payments/paypal/create-order`, {
                                cart_session_id: getSessionId(),
                                customer_name: customerInfo.name,
                                customer_email: customerInfo.email,
                                customer_phone: customerInfo.phone,
                                state_code: selectedState
                              });
                              return response.data.id;
                            } catch (error) {
                              console.error('Error creating PayPal order:', error);
                              toast.error('Failed to create payment. Please try again.');
                              throw error;
                            }
                          }}
                          onApprove={async (data, actions) => {
                            setProcessing(true);
                            try {
                              // Capture via backend
                              const response = await axios.post(`${API}/payments/paypal/capture-order`, {
                                order_id: data.orderID,
                                cart_session_id: getSessionId(),
                                customer_name: customerInfo.name,
                                customer_email: customerInfo.email,
                                customer_phone: customerInfo.phone,
                                state_code: selectedState
                              });
                              
                              if (response.data.success) {
                                setSavedAmount(calculateDiscount());
                                localStorage.removeItem('cart_session_id');
                                localStorage.setItem('cart_count', '0');
                                window.dispatchEvent(new Event('cartUpdated'));
                                setOrderNumber(response.data.order_number);
                                setOrderComplete(true);
                                toast.success('Payment Successful!');
                              } else {
                                throw new Error('Payment capture failed');
                              }
                            } catch (error) {
                              console.error('Error processing PayPal order:', error);
                              toast.error(error.response?.data?.detail || 'Failed to process payment');
                            } finally {
                              setProcessing(false);
                            }
                          }}
                          onError={(err) => {
                            console.error('PayPal Error:', err);
                            toast.error('PayPal payment failed. Please try again or use card payment.');
                          }}
                          onCancel={() => {
                            toast.info('Payment cancelled');
                          }}
                          disabled={processing}
                        />
                      </PayPalScriptProvider>
                    )}
                  </>
                )}

                {processing && (
                  <div className="text-center mt-4">
                    <div className="flex items-center justify-center gap-3 text-[#3B82F6]">
                      <div className="w-5 h-5 border-2 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
                      Processing your payment...
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 sm:p-8 sticky top-24">
                <h2 className="text-xl sm:text-2xl font-bold text-[var(--text-primary)] mb-6 flex items-center gap-3">
                  <ShoppingBag size={24} className="text-[#F97316]" />
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="pb-4 border-b border-[var(--border)]">
                      <h3 className="text-[var(--text-primary)] font-semibold mb-1">{item.service_name}</h3>
                      <p className="text-[#8B5CF6] text-sm mb-2">{item.package_name} Package</p>
                      {isOfferActive ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[var(--text-muted)] line-through text-sm">${item.price.toLocaleString()}</span>
                          <span className="text-[#10B981] font-semibold">${getDiscountedPrice(item.price).toLocaleString()}</span>
                        </div>
                      ) : (
                        <p className="text-[var(--text-secondary)]">${item.price.toLocaleString()}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-6 pb-6 border-b border-[var(--border)]">
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span>Subtotal</span>
                    <span>${calculateSubtotal().toLocaleString()}</span>
                  </div>
                  
                  {isOfferActive && (
                    <div className="flex justify-between">
                      <span className="flex items-center gap-2 text-[#10B981]">
                        <Tag size={14} />
                        {DISCOUNT_PERCENT}% Discount
                      </span>
                      <span className="text-[#10B981] font-semibold">-${calculateDiscount().toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-[var(--text-secondary)]">
                    <span className="flex items-center gap-1">
                      Tax 
                      {taxInfo ? (
                        <span className="text-xs text-[var(--text-muted)]">({taxInfo.tax_rate_percent})</span>
                      ) : (
                        <span className="text-xs text-[var(--text-muted)]">(Select state)</span>
                      )}
                    </span>
                    <span>
                      {calculatingTax ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        `$${calculateTaxAmount().toFixed(2)}`
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between text-xl font-bold">
                  <span className="text-[var(--text-primary)]">Total</span>
                  <div className="text-right">
                    {isOfferActive && (
                      <div className="text-[var(--text-muted)] line-through text-sm font-normal">
                        ${(calculateSubtotal() * (1 + getTaxRate())).toFixed(2)}
                      </div>
                    )}
                    <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                      ${calculateTotal().toFixed(2)}
                    </span>
                  </div>
                </div>

                {isOfferActive && (
                  <div className="mt-4 p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl text-center">
                    <p className="text-[#10B981] text-sm font-medium">
                      Saving ${calculateDiscount().toFixed(2)} today!
                    </p>
                  </div>
                )}

                {taxInfo && taxInfo.tax_rate === 0 && (
                  <div className="mt-4 p-3 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-xl text-center">
                    <p className="text-[#3B82F6] text-sm font-medium">
                      No sales tax in {taxInfo.state_name}!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Checkout;
